import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Search, MapPin, Clock, Filter, ChevronDown, Navigation, Loader2, Store, ShoppingCart, Leaf, Apple, Beef, Milk, Croissant, Wine as WineIcon, Package, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import gustoAvatar from "@/assets/gusto-avatar.png";
import gustoAvatarWhite from "@/assets/gusto-avatar-white.png";

const CATEGORIES = [
  { id: "all", label: "Tous", icon: Store },
  { id: "restaurant", label: "Restaurants", icon: Store },
  { id: "supermarche", label: "Supermarchés", icon: ShoppingCart },
  { id: "producteur", label: "Producteurs", icon: Leaf },
  { id: "primeur", label: "Primeurs", icon: Apple },
  { id: "boulangerie", label: "Boulangeries", icon: Croissant },
  { id: "boucherie", label: "Boucheries", icon: Beef },
  { id: "fromagerie", label: "Fromageries", icon: Milk },
  { id: "bio", label: "Bio & Marché", icon: Leaf },
  { id: "epicerie", label: "Épiceries", icon: Store },
  { id: "caviste", label: "Cavistes", icon: WineIcon },
  { id: "grossiste", label: "Grossistes", icon: Package },
  { id: "discount", label: "Discount", icon: Tag },
];

const RADIUS_OPTIONS = [
  { value: 0, label: "Tout" },
  { value: 5, label: "5 km" },
  { value: 10, label: "10 km" },
  { value: 20, label: "20 km" },
  { value: 50, label: "50 km" },
];

const StoreCard = ({ store }) => {
  const isClaimed = store.claimed;
  return (
    <Link to={store.is_pro ? `/business/${store.id}` : `/stores/${store.id}`} data-testid={`store-card-${store.id}`}>
      <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
        {/* Cover */}
        <div className="aspect-[16/9] bg-gray-100 relative overflow-hidden">
          {store.cover_image ? (
            <img src={store.cover_image} alt={store.name} className={`w-full h-full object-cover ${!isClaimed ? "blur-[6px] scale-105" : ""}`} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
              <img src={gustoAvatarWhite} alt="" className="w-16 h-16 opacity-20" />
            </div>
          )}
          {/* Salmon overlay */}
          <div className="absolute inset-0 bg-primary/25" />
          {!isClaimed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <img src={gustoAvatarWhite} alt="" className="w-10 h-10 opacity-40 drop-shadow-lg" />
            </div>
          )}
          {store.is_pro && (
            <Badge className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px]">
              Pro Gusto
            </Badge>
          )}
          {!isClaimed && !store.is_pro && (
            <Badge className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-[10px]">
              En attente
            </Badge>
          )}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent h-12" />
        </div>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            {/* Logo */}
            <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden -mt-6 relative z-10 shadow-sm">
              {store.logo_url ? (
                <img src={store.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
              ) : (
                <img src={gustoAvatarWhite} alt="" className="w-6 h-6 opacity-20" />
              )}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h3 className="font-heading font-semibold text-sm text-charcoal truncate">{store.name}</h3>
              <p className="text-[11px] text-muted-foreground truncate">{store.category_label}</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {store.cuisine_type && (
                  <span className="bg-amber-100 text-amber-700 px-1.5 py-0 rounded text-[9px] font-medium">{store.cuisine_type}</span>
                )}
                {store.pmr_accessible && (
                  <span className="bg-blue-100 text-blue-700 px-1.5 py-0 rounded text-[9px]">♿ PMR</span>
                )}
                {store.has_click_collect && (
                  <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0 rounded text-[9px]">Click&Collect</span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              {store.address}, {store.postal_code} {store.city}
            </p>
            {store.hours_today && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3 flex-shrink-0" />
                {store.hours_today}
              </p>
            )}
          </div>
          {store.distance_km != null && (
            <Badge variant="outline" className="mt-2 text-[10px]">
              <Navigation className="w-2.5 h-2.5 mr-1" />
              {store.distance_km < 1 ? `${Math.round(store.distance_km * 1000)}m` : `${store.distance_km.toFixed(1)} km`}
            </Badge>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

const StoresDirectoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [postalCode, setPostalCode] = useState(searchParams.get("cp") || "");
  const [category, setCategory] = useState(searchParams.get("cat") || "all");
  const [radius, setRadius] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const fetchStores = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (params.search || search) query.set("search", params.search || search);
      if (params.postal_code || postalCode) query.set("postal_code", params.postal_code || postalCode);
      if ((params.category || category) && (params.category || category) !== "all") query.set("category", params.category || category);
      const rad = params.radius !== undefined ? params.radius : radius;
      if (rad > 0) query.set("radius_km", rad);
      if (params.lat) query.set("lat", params.lat);
      if (params.lng) query.set("lng", params.lng);
      const res = await axios.get(`${API}/stores/directory?${query.toString()}`);
      setStores(res.data);
    } catch {
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, [search, postalCode, category, radius]);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStores();
  };

  const handleCategoryChange = (cat) => {
    setCategory(cat);
    fetchStores({ category: cat });
  };

  const handleGeolocate = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        fetchStores({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoLoading(false);
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto" data-testid="stores-directory">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-charcoal">Commerces autour de vous</h1>
          <p className="text-sm text-muted-foreground mt-1">Trouvez les meilleurs commerces alimentaires</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleGeolocate}
          disabled={geoLoading}
          className="rounded-full gap-1.5"
          data-testid="geolocate-btn"
        >
          {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
          Autour de moi
        </Button>
      </div>

      {/* Search + Postal Code */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un commerce..."
            className="pl-9"
            data-testid="store-search"
          />
        </div>
        <Input
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          placeholder="Code postal"
          className="w-32"
          data-testid="postal-code-input"
        />
        <Button type="submit" size="sm" className="rounded-full px-4">
          <Search className="w-4 h-4" />
        </Button>
      </form>

      {/* Radius filter */}
      <div className="flex items-center gap-2 mb-3" data-testid="radius-filter">
        <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <span className="text-xs text-muted-foreground whitespace-nowrap">Rayon :</span>
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
          {RADIUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setRadius(opt.value); fetchStores({ radius: opt.value }); }}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                radius === opt.value ? 'bg-primary text-white' : 'bg-gray-100 text-charcoal hover:bg-gray-200'
              }`}
              data-testid={`radius-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const isActive = category === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-charcoal hover:bg-gray-200"
              }`}
              data-testid={`cat-${cat.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : stores.length === 0 ? (
        <div className="text-center py-16">
          <Store className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Aucun commerce trouvé</p>
          <p className="text-xs text-muted-foreground mt-1">Essayez un autre code postal ou une autre catégorie</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">{stores.length} commerce{stores.length > 1 ? "s" : ""} trouvé{stores.length > 1 ? "s" : ""}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stores.map((store, i) => (
              <motion.div key={store.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <StoreCard store={store} />
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default StoresDirectoryPage;
